import Vue from "vue";
import Vuex from "vuex";
import Helpers from "@/services/Helpers.js";
import LiberatorService from "@/services/LiberatorService.js";
import moment from "moment";
import { DialogProgrammatic as Dialog } from "buefy";

Vue.use(Vuex);

function getUnexpectedError(errorResponse) {
  return `An unexpected error occurred -- (${errorResponse.response.status}, ${
    errorResponse.response.statusText
  }) - ${errorResponse.response.data}`;
}

function errorMessage(context, message) {
  Dialog.alert({
    title: "",
    message: message,
    type: "is-danger",
    hasIcon: true,
    icon: "times-circle",
    iconPack: "fa",
    ariaRole: "alertdialog",
    ariaModal: true
  });
  context.commit("SET_LOADING_STATUS", false);
  context.commit("SET_LIBERATOR_ERROR", message);
}

function handleError(context, errorResponse) {
  if (
    errorResponse &&
    errorResponse.response &&
    errorResponse.response.status &&
    errorResponse.response.status == 400
  )
    errorMessage(context, errorResponse.response.data);
  else if (
    errorResponse &&
    errorResponse.config &&
    errorResponse.config.crossdomain
  ) {
    // Handle the case if a timeout occurred on the lambda service.
    errorMessage(
      context,
      "Service is running slow or disabled, please try again"
    );
    context.commit("SET_LOADING_STATUS", false);
    context.commit("SET_LIBERATOR_ERROR", getUnexpectedError(errorResponse));
  } else if (
    errorResponse &&
    errorResponse.response &&
    errorResponse.response.data
  ) {
    errorMessage(errorResponse.data);
    context.commit("SET_LOADING_STATUS", false);
    context.commit("SET_LIBERATOR_ERROR", getUnexpectedError(errorResponse));
  } else {
    errorMessage(errorResponse);
    context.commit("SET_LOADING_STATUS", false);
    context.commit("SET_LIBERATOR_ERROR", getUnexpectedError(errorResponse));
  }
}

export default new Vuex.Store({
  state: {
    // State.
    isLoading: false,
    isLoadingWaiting: false,
    isUploading: false,
    isAddDocumentsActive: false,
    isLoginActive: true,
    saleCompShowPhotos: false,
    saleCompShowDetails: false,
    uploadPercentage: 0,
    isPreferenceUpdated: true,
    isEmailEvaluationActive: false,
    isEmailBpoActive: false,
    isEmailBpo2Active: false,
    isShareEvaluationActive: false,
    isEvaluating: false,
    isLoginRequired: true,
    reactivateUserId: undefined,
    isNewUserActive: false,
    isPdf: false,
    isPublicListing: false,
    isNewMultifamilyEvaluationActive: false,
    apiVersion: "0.0",
    isRentCompsActive: false,
    isNewSinglefamilyProperty: false,
    isNewMultifamilyProperty: false,
    isEditMFFPropertyActive: false,

    // Messages.
    liberatorError: "",
    signinError: "",
    liberatorSuccess: "",

    // Objects.
    session: "",
    property: "",
    properties: [],
    connections: [],
    propertiesFilter: {
      searchTerm: "",
      stages: [
        "None",
        "Finding",
        "Evaluating",
        "Negotiating",
        "Diligence",
        "Closing",
        "Rehabbing",
        "Leasing",
        "Selling"
      ],
      startDate: Helpers.addMonths(new Date(), -1),
      endDate: new Date(),
      useDates: false
    },
    propertyId: "",
    evaluation: "",
    searchTypes: [],
    mlsMarkets: [],
    states: [],
    chargifyUrl: "",
    forSales: [],
    searchHistory: [],
    breadCrumbs: [
      {
        description: "Properties",
        path: "path"
      },
      {
        description: "Details",
        path: "path"
      },
      {
        description: "Evaluation",
        path: "path"
      }
    ],
    allUsers: [],
    defaultPreferences: [],
    evaluationUsage: [],
    evaluationUsageFilter: {
      //startDate: Helpers.addMonths(new Date(), -1),
      startDate: new Date(
        new Date().getFullYear() +
          "/" +
          (new Date().getMonth() + 1) +
          "/" +
          new Date().getDate()
      ),
      endDate: new Date(
        new Date().getFullYear() +
          "/" +
          (new Date().getMonth() + 1) +
          "/" +
          new Date().getDate()
      )
    },
    connectionEdit: {
      connection: {
        id: "",
        type: "",
        name: "",
        email: "",
        phone: ""
      },
      isActive: false,
      mode: "new" // either new or edit.
    },
    notes: [],
    noteEdit: {
      note: {
        id: "",
        propertyId: "",
        createdUtc: new Date(),
        stage: "",
        theNote: ""
      },
      isActive: false
    },
    propertyAttributeEdit: {
      attributes: {
        baths: "",
        beds: "",
        county: "",
        garage: "",
        hoaAnnual: "",
        listDate: "",
        listPrice: "",
        sqft: "",
        subdivision: "",
        taxesAnnual: "",
        yearBuilt: ""
      },
      isActive: false
    },
    listing: {
      attributes: {
        applicationPolicy: "",
        availableDate: new Date(),
        baths: 0,
        beds: 0,
        city: "",
        depositPolicy: "",
        description: "",
        features: [],
        id: "",
        isActive: false,
        mapUrl: "",
        petPolicy: "",
        pictureUrls: [],
        publicLink: "",
        rentPolicy: "",
        sqft: 0,
        state: "",
        street: "",
        title: "",
        zip: ""
      },
      isActive: false,
      isNew: false,
      isPublicLinkActive: false
    },
    bpo: {
      inputs: {
        askingPrice_E17: 0,
        offerPrice_E18: 0,
        closingCostPercent_D21: 0,
        operatingCapital_E22: 0,
        operatingCapitalAutocalc: true,
        downPaymentPercent_E28: 0,
        interestRatePercent_E30: 0,
        termInYears_E31: 0,
        totalRehab_E33: 0,
        percentOfRehabToBeFinanced_E34: 0,
        assetManagementPercent_D63: 0,
        leadInvestorCompensationPercent_D72: 0,
        notes_I45: "",
        actuals: {
          economicVacancyRate_K41: 0,
          grossRentalIncome_E38: 0,
          laundry_E42: 0,
          miscellaneousOtherIncome_E43: 0,
          expensesPerUnit_E47: 0,
          capitalReservesPerUnit_E57: 0
        },
        brokers: {
          economicVacancyRate_K42: 0,
          grossRentalIncome_F38: 0,
          laundry_F42: 0,
          miscellaneousOtherIncome_F43: 0,
          expensesPerUnit_F47: 0,
          capitalReservesPerUnit_F57: 0,
          capRateProjection_F76: 0
        },
        proformas: {
          economicVacancyRate_K43: 0,
          grossRentalIncome_G38: 0,
          laundry_G42: 0,
          miscellaneousOtherIncome_G43: 0,
          expensesPerUnit_G47: 0,
          capitalReservesPerUnit_G57: 0,
          capRateProjection_G76: 0,
          rentIncreasePercent_O26: 0
        }
      }
    },
    bpo2: {},
    longform: {},
    rentComps: [],
    naaSurveys: [],
    rentCompPropertyId: "",
    // bpoUnitTypes: [
    //   "Efficiency/Studio",
    //   "1 Bed, 1 Bath",
    //   "2 Beds, 1 Bath",
    //   "2 Beds, 2 Baths",
    //   "3 Beds, 1 Bath",
    //   "3 Beds, 2 Baths"
    // ]
    bpoUnitTypes: [
      "Eff",
      "1-1",
      "2-1",
      "2-1.5",
      "2-2",
      "3-1",
      "3-1.5",
      "3-2",
      "3-2.5"
    ]
  },

  getters: {
    session: state => {
      // If the state.session has a value, return it.
      if (state.session != "") return state.session;

      // If the sessionStorage has a value, then set the state.session
      // and return that.
      if (LiberatorService.GetSession() != "") {
        state.session = LiberatorService.GetSession();
        return state.session;
      }

      // Neither state.session or sessionStage have a value so return nothing.
      return "";
    },

    sessionKey: () => {
      if (LiberatorService.GetSession() == "") return "";
      return LiberatorService.GetSession().sessionKey;
    },

    search: state => {
      if (state.session)
        if (state.session.rights.search || state.session.rights.searchFree)
          return true;
        else return false;
    }
  },

  mutations: {
    SET_API_VERSION(state, apiVersion) {
      state.apiVersion = apiVersion;
    },

    CLEAR_LIBERATOR_ERROR(state) {
      state.liberatorError = "";
    },

    CLEAR_LIBERATOR_SUCCESS(state) {
      state.liberatorSuccess = "";
    },

    SET_SIGNIN_ERROR(state, error) {
      state.signinError = error;
    },

    SET_SESSION(state, session) {
      state.session = session;
      sessionStorage.session = JSON.stringify(session);
    },

    CLEAR_SESSION(state) {
      sessionStorage.session = "";
      state.session = "";
    },

    SET_UPLOAD_STATUS(state, isUploading) {
      state.isUploading = isUploading;
    },

    SET_LOADING_STATUS(state, isLoading) {
      if (isLoading) {
        state.isLoadingWaiting = true;
        setTimeout(() => {
          if (state.isLoadingWaiting) state.isLoading = true;
          else state.isLoading = false;
        }, 1000);
      } else {
        state.isLoadingWaiting = false;
        state.isLoading = isLoading;
      }
    },

    SET_LOADING_STATUS_NODELAY(state, isLoading) {
      state.isLoading = isLoading;
    },

    SET_PROPERTIES(state, properties) {
      state.properties = properties;
    },

    SET_PROPERTY(state, property) {
      state.property = property;
    },

    SET_LIBERATOR_ERROR(state, error) {
      state.liberatorError = error;
    },

    SET_LIBERATOR_SUCCESS(state, message) {
      state.liberatorSuccess = message;
    },

    SET_REACTIVATION_USERID(state, userId) {
      state.reactivateUserId = userId;
    },

    INSERT_PROPERTY: (state, property) => {
      if (!state.properties) state.properties = [];
      state.properties.unshift(property);
    },

    ERROR_PROPERTIES(state, obj) {
      state.errorProperties = obj;
    },

    ERROR_PROPERTY_DETAILS(state, obj) {
      state.errorPropertyDetails = obj;
    },

    APPEND_NOTE: (state, note) => {
      state.property.notes.push(note);
    },

    INSERT_NOTE: (state, note) => {
      state.property.notes.unshift(note);
    },

    DELETE_NOTE: (state, noteId) => {
      if (state.property && state.property.notes) {
        var index = state.property.notes.findIndex(n => n.id == noteId);
        if (index > -1) state.property.notes.splice(index, 1);
      }
      if (state.notes) {
        index = state.notes.findIndex(n => n.id == noteId);
        if (index > -1) state.notes.splice(index, 1);
      }
    },

    INSERT_EVALUATION: (state, evaluation) => {
      if (!state.property.evaluations) state.property.evaluations = [];
      state.property.evaluations.unshift(evaluation);
    },

    DELETE_EVALUATION: (state, evaluation) => {
      var index = state.property.evaluations.findIndex(
        e => e.id == evaluation.id
      );
      if (index > -1) state.property.evaluations.splice(index, 1);
    },

    UPDATE_EVALUATION: (state, evaluation) => {
      state.evaluation = evaluation;
    },

    SET_CONNECTIONS: (state, connections) => {
      state.connections = connections;
    },

    SET_SEARCH_TYPES: (state, searchTypes) => {
      state.searchTypes = searchTypes;
    },

    SET_MLS_MARKETS: (state, mlsMarkets) => {
      state.mlsMarkets = mlsMarkets;
    },

    SET_STATES: (state, states) => {
      state.states = states;
    },

    SET_SESSION_USER: (state, user) => {
      state.session.user = user;
    },

    SET_CHARGIFY_URL: (state, chargifyUrl) => {
      state.chargifyUrl = chargifyUrl;
    },

    SET_FORSALES: (state, forSales) => {
      state.forSales = forSales;
    },

    SET_SEARCH_HISTORY: (state, searchHistory) => {
      state.searchHistory = searchHistory;
    },

    SET_IS_EVALUATING: (state, value) => {
      state.isEvaluating = value;
    },

    SET_ALL_USERS: (state, users) => {
      state.allUsers = users;
    },

    UPDATE_USER_RIGHTS: (state, payload) => {
      var foundIndex = state.allUsers.findIndex(u => u.id == payload.userId);
      if (foundIndex) state.allUsers[foundIndex].rights = payload.data;
    },

    SET_DEFAULT_PREFERENCES: (state, defaultPreferences) => {
      state.defaultPreferences = defaultPreferences;
    },

    SET_EVALUATION_USAGE: (state, evaluationUsage) => {
      state.evaluationUsage = evaluationUsage;
    },

    SET_CONNECTION_EDIT_ISACTIVE: (state, isActive) => {
      state.connectionEdit.isActive = isActive;
    },

    SET_CONNECTION_EDIT_CONNECTION: (state, connection) => {
      state.connectionEdit.connection = connection;
    },

    SET_NEW_USER_ACTIVE: (state, isActive) => {
      state.isNewUserActive = isActive;
    },

    SET_NOTES: (state, notes) => {
      state.notes = notes;
    },

    SET_NOTE_EDIT_ISACTIVE: (state, isActive) => {
      state.noteEdit.isActive = isActive;
    },

    SET_CONVENTIONAL_SHOW: (state, show) => {
      state.evaluation.calculator.conventionalInputs.show = show;
    },

    SET_HARDMONEY_SHOW: (state, show) => {
      state.evaluation.calculator.hardMoneyInputs.show = show;
    },

    SET_PROPERTY_ATTRIBUTES: (state, isActive) => {
      state.propertyAttributeEdit.isActive = isActive;
    },

    SET_LISTING_ATTRIBUTES: (state, isActive) => {
      state.listing.isActive = isActive;
    },

    UPDATE_LISTING: (state, listing) => {
      state.listing.attributes = listing;

      var foundIndex = state.property.listings.findIndex(
        x => x.id == listing.id
      );

      if (foundIndex >= 0) {
        state.property.listings.splice(foundIndex, 1);
        state.property.listings.unshift(listing);

        state.property.listings.sort(
          (a, b) => new Date(b.availableDate) - new Date(a.availableDate)
        );
      }
    },

    ADD_LISTING: (state, listing) => {
      state.listing.attributes = listing;
      state.property.listings.unshift(listing);
      state.property.listings.sort(
        (a, b) => new Date(b.availableDate) - new Date(a.availableDate)
      );
    },

    SET_LISTING: (state, listing) => {
      state.listing.attributes = listing;
    },

    SET_BPO: (state, bpo) => {
      state.bpo = bpo;
    },

    SET_BPO2: (state, bpo2) => {
      state.bpo2 = bpo2;
    },

    SET_LONGFORM: (state, longform) => {
      state.longform = longform;
    },

    SET_RENTCOMPS: (state, rentComps) => {
      state.rentComps = rentComps;
    },

    SET_BPO_BY_GUID: (state, bpoByGuid) => {
      state.bpo = bpoByGuid.bpo;
      state.property = bpoByGuid.property;
    },

    SET_BPO2_BY_GUID: (state, bpo2ByGuid) => {
      state.bpo2 = bpo2ByGuid.bpo2;
      state.property = bpo2ByGuid.property;
    },

    SET_NAA_SURVEYS: (state, naaSurveys) => {
      state.naaSurveys = naaSurveys;
    }
  },

  actions: {
    async getVersion(context) {
      if (context.state.version == undefined) {
        await LiberatorService.getVersion().then(response => {
          context.commit("SET_API_VERSION", response.data);
        });
      }
    },

    async getProperties(context) {
      context.commit("SET_LOADING_STATUS", true);
      await LiberatorService.getProperties(
        context.state.propertiesFilter.searchTerm,
        context.state.propertiesFilter.stages,
        context.state.propertiesFilter.useDates,
        context.state.propertiesFilter.startDate,
        context.state.propertiesFilter.endDate
      )
        .then(response => {
          context.commit("SET_LOADING_STATUS", false);
          context.commit("SET_PROPERTIES", response.data);
        })
        .catch(error => {
          context.commit("SET_LOADING_STATUS", false);
          return context.commit(
            "SET_LIBERATOR_ERROR",
            getUnexpectedError(error)
          );
        });
    },

    async getProperty(context, propertyId) {
      context.commit("SET_LOADING_STATUS", true);
      await LiberatorService.getProperty(propertyId)
        .then(async response => {
          await context.commit("SET_LOADING_STATUS", false);
          await context.commit("SET_PROPERTY", response.data);
        })
        .catch(error => {
          context.commit("SET_LOADING_STATUS", false);
          return context.commit(
            "SET_LIBERATOR_ERROR",
            getUnexpectedError(error)
          );
        });
    },

    async newMultifamilyProperty(context, property) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      return await new Promise(async (resolve, reject) => {
        await LiberatorService.newMultifamilyProperty(property)
          .then(async response => {
            await context.commit("SET_LOADING_STATUS", false);
            await context.commit("INSERT_PROPERTY", response.data);
            await context.commit("SET_PROPERTY", response.data);
            resolve(response);
          })
          .catch(async error => {
            await context.commit("SET_LOADING_STATUS", false);
            await context.commit(
              "SET_LIBERATOR_ERROR",
              `Can't find property ${property.address}, ${property.city}, ${
                property.state
              }, ${property.zip}`
            );
            reject(error);
          });
      });
    },

    async updateMultifamilyPropertyAttributes(context, property) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      return await new Promise(async (resolve, reject) => {
        await LiberatorService.updateMultifamilyPropertyAttributes(property)
          .then(async response => {
            await context.commit("SET_LOADING_STATUS", false);
            await context.commit("SET_PROPERTY", response.data);
            resolve(response);
          })
          .catch(async error => {
            await context.commit("SET_LOADING_STATUS", false);
            await context.commit(
              "SET_LIBERATOR_ERROR",
              `Can't find property ${property.address}, ${property.city}, ${
                property.state
              }, ${property.zip}`
            );
            reject(error);
          });
      });
    },

    async newPropertyByAddress(
      context,
      { address, city, state, zip, latitude, longitude }
    ) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      return await new Promise(async (resolve, reject) => {
        await LiberatorService.newPropertyByAddress(
          address,
          city,
          state,
          zip,
          latitude,
          longitude
        )
          .then(async response => {
            await context.commit("SET_LOADING_STATUS", false);
            await context.commit("INSERT_PROPERTY", response.data);
            await context.commit("SET_PROPERTY", response.data);
            resolve(response);
          })
          .catch(async error => {
            await context.commit("SET_LOADING_STATUS", false);
            await context.commit(
              "SET_LIBERATOR_ERROR",
              `Can't find property ${address}, ${city}, ${state}, ${zip}`
            );
            reject(error);
          });
      });
    },

    async newPropertyByMlsNumber(context, { mlsMarket, mlsNumber }) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      return await new Promise(async (resolve, reject) => {
        await LiberatorService.newPropertyByMlsNumber(mlsMarket, mlsNumber)
          .then(async response => {
            await context.commit("SET_LOADING_STATUS", false);
            await context.commit("INSERT_PROPERTY", response.data);
            await context.commit("SET_PROPERTY", response.data);
            resolve(response);
          })
          .catch(async error => {
            await context.commit("SET_LOADING_STATUS", false);
            reject(error);
            return await context.commit(
              "SET_LIBERATOR_ERROR",
              `Can't create the new property because the MLS # ${mlsNumber} doesn't exist in the ${mlsMarket} market.`
            );
          });
      });
    },

    async newPropertyNote(context, { propertyId, stage, note }) {
      context.commit("SET_LOADING_STATUS", true);
      await LiberatorService.newPropertyNote(propertyId, stage, note)
        .then(async response => {
          await context.commit("SET_LOADING_STATUS", false);
          await context.commit("INSERT_NOTE", response.data);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    updatePropertyNote(context, { propertyId, note }) {
      context.commit("SET_LOADING_STATUS", true);
      LiberatorService.updatePropertyNote(propertyId, note)
        .then(() => {
          context.commit("SET_LOADING_STATUS", false);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    deletePropertyNote(context, { propertyId, noteId }) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      LiberatorService.deletePropertyNote(propertyId, noteId)
        .then(() => {
          context.commit("SET_LOADING_STATUS", false);
          context.commit("DELETE_NOTE", noteId);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    async newPropertyEvaluation(context, propertyId) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      return await new Promise(async (resolve, reject) => {
        await LiberatorService.newPropertyEvaluation(propertyId)
          .then(async response => {
            await context.commit("INSERT_EVALUATION", response.data);
            await context.commit("UPDATE_EVALUATION", response.data);
            await context.commit("SET_LOADING_STATUS", false);
            resolve(response);
          })
          .catch(async error => {
            await context.commit("SET_LOADING_STATUS", false);
            reject(error);
            return await handleError(context, error);
          });
      });
    },

    deletePropertyEvaluation(context, evaluation) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      LiberatorService.deletePropertyEvaluation(
        evaluation.propertyId,
        evaluation.id
      )
        .then(() => {
          context.commit("SET_LOADING_STATUS", false);
          context.commit("DELETE_EVALUATION", evaluation);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    updateEvaluation(context, evaluation) {
      context.commit("UPDATE_EVALUATION", evaluation);
    },

    updatePropertyEvaluationSaleComps(context, evaluation) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      LiberatorService.updatePropertyEvaluationSaleComps(
        evaluation.propertyId,
        evaluation.id,
        evaluation.saleCompGroup.saleCompInputs.searchType,
        evaluation.saleCompGroup.saleCompInputs.searchTerm,
        evaluation.saleCompGroup.saleCompInputs.sqftPlusMinus,
        evaluation.saleCompGroup.saleCompInputs.bedsMin,
        evaluation.saleCompGroup.saleCompInputs.bedsMax,
        evaluation.saleCompGroup.saleCompInputs.bathsMin,
        evaluation.saleCompGroup.saleCompInputs.bathsMax,
        evaluation.saleCompGroup.saleCompInputs.garageMin,
        evaluation.saleCompGroup.saleCompInputs.garageMax,
        evaluation.saleCompGroup.saleCompInputs.yearBuiltPlusMinus,
        evaluation.saleCompGroup.saleCompInputs.monthsClosed,
        evaluation.saleCompGroup.saleCompInputs.confineToCounty,
        evaluation.saleCompGroup.saleCompInputs.confineToZip,
        evaluation.saleCompGroup.saleCompInputs
          .ignoreParametersExceptMonthsClosed
      )
        .then(response => {
          context.commit("SET_LOADING_STATUS", false);
          context.commit("UPDATE_EVALUATION", response.data);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    updatePropertyEvaluationRentComps(context, evaluation) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      LiberatorService.updatePropertyEvaluationRentComps(
        evaluation.propertyId,
        evaluation.id,
        evaluation.rentCompGroup.rentCompInputs.searchType,
        evaluation.rentCompGroup.rentCompInputs.searchTerm,
        evaluation.rentCompGroup.rentCompInputs.sqftPlusMinus,
        evaluation.rentCompGroup.rentCompInputs.bedsMin,
        evaluation.rentCompGroup.rentCompInputs.bedsMax,
        evaluation.rentCompGroup.rentCompInputs.bathsMin,
        evaluation.rentCompGroup.rentCompInputs.bathsMax,
        evaluation.rentCompGroup.rentCompInputs.garageMin,
        evaluation.rentCompGroup.rentCompInputs.garageMax,
        evaluation.rentCompGroup.rentCompInputs.yearBuiltPlusMinus,
        evaluation.rentCompGroup.rentCompInputs.monthsClosed,
        evaluation.rentCompGroup.rentCompInputs.confineToCounty,
        evaluation.rentCompGroup.rentCompInputs.confineToZip,
        evaluation.rentCompGroup.rentCompInputs
          .ignoreParametersExceptMonthsClosed
      )
        .then(response => {
          context.commit("SET_LOADING_STATUS", false);
          context.commit("UPDATE_EVALUATION", response.data);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    includeExcludePropertyEvaluationSaleComp(
      context,
      { propertyId, evaluationId, saleCompId, include }
    ) {
      LiberatorService.includeExcludePropertyEvaluationSaleComp(
        propertyId,
        evaluationId,
        saleCompId,
        include
      )
        .then(response => {
          context.commit("UPDATE_EVALUATION", response.data);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    includeExcludePropertyEvaluationRentComp(
      context,
      { propertyId, evaluationId, rentCompId, include }
    ) {
      LiberatorService.includeExcludePropertyEvaluationRentComp(
        propertyId,
        evaluationId,
        rentCompId,
        include
      )
        .then(response => {
          context.commit("UPDATE_EVALUATION", response.data);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    async updatePropertyEvaluationCalculator(context, evaluation) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      await LiberatorService.updatePropertyEvaluationCalculator(
        evaluation.propertyId,
        evaluation.id,
        evaluation.calculator.dealTermInputs.estimatedMarketValue,
        evaluation.calculator.dealTermInputs.estimatedAppraisedValue,
        evaluation.calculator.dealTermInputs.purchasePrice,
        evaluation.calculator.dealTermInputs.sellerContribution,
        evaluation.calculator.dealTermInputs.survey,
        evaluation.calculator.dealTermInputs.appraisal,
        evaluation.calculator.dealTermInputs.inspection,
        evaluation.calculator.dealTermInputs.repairsMakeReady,
        evaluation.calculator.dealTermInputs.hoaAnnual,
        evaluation.calculator.dealTermInputs.propertyTaxAnnual,
        evaluation.calculator.dealTermInputs.propertyInsuranceAnnual,
        evaluation.calculator.dealTermInputs.miscellaneousMonthly,
        evaluation.calculator.dealTermInputs.rent,
        evaluation.calculator.dealTermInputs.maxRefiCashback,
        evaluation.calculator.conventionalInputs.downPaymentPercent,
        evaluation.calculator.conventionalInputs.loanTermInYears,
        evaluation.calculator.conventionalInputs.interestRatePercent,
        evaluation.calculator.conventionalInputs.lenderAndTitleFees,
        evaluation.calculator.conventionalInputs.monthsTaxAndInsurance,
        evaluation.calculator.conventionalInputs.mortgageInsuranceAnnual,
        evaluation.calculator.conventionalInputs.show,
        evaluation.calculator.hardMoneyInputs.hardLoanToValuePercent,
        evaluation.calculator.hardMoneyInputs.hardLenderAndTitleFees,
        evaluation.calculator.hardMoneyInputs.hardInterestRate,
        evaluation.calculator.hardMoneyInputs.hardMonthsToRefinance,
        evaluation.calculator.hardMoneyInputs.hardRollInLenderFees,
        evaluation.calculator.hardMoneyInputs.hardWeeksUntilLeased,
        evaluation.calculator.hardMoneyInputs.refinanceLoanToValuePercent,
        evaluation.calculator.hardMoneyInputs.refinanceLoanTermInYears,
        evaluation.calculator.hardMoneyInputs.refinanceInterestRatePercent,
        evaluation.calculator.hardMoneyInputs.refinanceLenderAndTitleFees,
        evaluation.calculator.hardMoneyInputs.refinanceMonthsTaxAndInsurance,
        evaluation.calculator.hardMoneyInputs.refinanceMortgageInsuranceAnnual,
        evaluation.calculator.hardMoneyInputs.show
      )
        .then(async response => {
          await context.commit("UPDATE_EVALUATION", response.data);
          await context.commit("SET_LOADING_STATUS", false);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    updatePropertyStage(context, { propertyId, stage }) {
      LiberatorService.updatePropertyStage(propertyId, stage)
        .then(response => {
          context.commit("SET_PROPERTY", response.data);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    async getUserConnections(context, { userId, includeHidden }) {
      await LiberatorService.getUserConnections(userId, includeHidden)
        .then(async response => {
          await context.commit("SET_CONNECTIONS", response.data);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    addPropertyConnection(context, { property, connection }) {
      LiberatorService.addPropertyConnection(property.id, connection.id)
        .then(response => {
          context.commit("SET_PROPERTY", response.data);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    deletePropertyConnection(context, { property, connection }) {
      LiberatorService.deletePropertyConnection(property.id, connection.id)
        .then(response => {
          context.commit("SET_PROPERTY", response.data);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    async newUserConnection(context, { user, connection }) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      return await new Promise(async (resolve, reject) => {
        await LiberatorService.newUserConnection(
          user.id,
          connection.type,
          connection.name,
          connection.phone,
          connection.email
        )
          .then(async response => {
            connection.id = response.data;
            resolve(response);
            context.commit("SET_LOADING_STATUS_NODELAY", false);
          })
          .catch(error => {
            reject(error);
            context.commit("SET_LOADING_STATUS_NODELAY", false);
            return handleError(context, error);
          });
      });
    },

    async updateUserConnection(context, { userId, connection }) {
      return await new Promise(async (resolve, reject) => {
        await LiberatorService.updateUserConnection(userId, connection)
          .then(response => {
            resolve(response);
          })
          .catch(error => {
            reject(error);
            return handleError(context, error);
          });
      });
    },

    async newUserConnectionAddToProperty(
      context,
      { user, property, type, name, phone, email }
    ) {
      await LiberatorService.newUserConnectionAddToProperty(
        user.id,
        property.id,
        type,
        name,
        phone,
        email
      )
        .then(async response => {
          context.commit("SET_PROPERTY", response.data);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    async addDocumentsToProperty(context, { propertyId, files }) {
      context.commit("SET_UPLOAD_STATUS", true);
      await LiberatorService.addDocumentsToProperty(propertyId, files)
        .then(response => {
          context.commit("SET_PROPERTY", response.data);
          context.commit("SET_UPLOAD_STATUS", false);
        })
        .catch(error => {
          context.commit("SET_UPLOAD_STATUS", false);
          return handleError(context, error);
        });
    },

    async addDocument(context, { propertyId, name, type, s3Key, size }) {
      // context.commit("SET_UPLOAD_STATUS", true);
      await LiberatorService.addDocument(propertyId, name, type, s3Key, size)
        .then(response => {
          context.commit("SET_PROPERTY", response.data);
          // context.commit("SET_UPLOAD_STATUS", false);
        })
        .catch(error => {
          // context.commit("SET_UPLOAD_STATUS", false);
          return handleError(context, error);
        });
    },

    deleteDocument(context, { propertyId, documentId }) {
      LiberatorService.deleteDocument(propertyId, documentId)
        .then(response => {
          context.commit("SET_PROPERTY", response.data);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    updateDocumentName(context, { propertyId, documentId, documentName }) {
      LiberatorService.updateDocumentName(propertyId, documentId, documentName)
        .then(response => {
          context.commit("SET_PROPERTY", response.data);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    async signin(context, { email, password }) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);

      return new Promise((resolve, reject) => {
        var message = "";
        LiberatorService.signin(email, password)
          .then(response => {
            context.commit("SET_SESSION", response.data);
            context.commit("SET_LOADING_STATUS_NODELAY", false);
            context.dispatch("getProperties");
            resolve(response);
          })
          .catch(async error => {
            if (error.response.status == 403) reject(error);
            else if (error.response.status == 404)
              message = "Your email or password were entered incorrectly";
            else message = getUnexpectedError(error);
            await context.commit("SET_LOADING_STATUS", false);
            await context.commit("SET_LIBERATOR_ERROR", message);
            reject(message);
          });
      });
    },

    async createSharedSession(context, { guid, editKey }) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);

      return new Promise((resolve, reject) => {
        var message = "";
        LiberatorService.createSharedSession(guid, editKey)
          .then(response => {
            context.commit("SET_SESSION", response.data);
            context.commit("SET_LOADING_STATUS_NODELAY", false);
            // context.dispatch("getProperties");
            resolve(response);
          })
          .catch(async error => {
            if (error.response.status == 403) reject(error);
            else if (error.response.status == 404)
              message = "Your sharing link isn't valid";
            else message = getUnexpectedError(error);
            await context.commit("SET_LOADING_STATUS", false);
            await context.commit("SET_LIBERATOR_ERROR", message);
            reject(message);
          });
      });
    },

    copyEvaluation(context, { propertyId, evaluationId }) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      LiberatorService.copyEvaluation(propertyId, evaluationId)
        .then(response => {
          context.commit("INSERT_EVALUATION", response.data);
          context.commit("SET_LOADING_STATUS", false);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    updateEvaluationNotes(context, { propertyId, evaluationId, notes }) {
      LiberatorService.updateEvaluationNotes(propertyId, evaluationId, notes)
        .then(response => {
          context.commit("UPDATE_EVALUATION", response.data);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    async getSession(context, sessionKey) {
      await LiberatorService.getSession(sessionKey)
        .then(response => {
          context.commit("SET_SESSION", response.data);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    async createEvaluationPdf(context, { propertyId, evaluationId }) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      await LiberatorService.createEvaluationPdf(propertyId, evaluationId)
        .then(response => {
          const file = new Blob([response.data], {
            type: "application/pdf"
          });
          const fileURL = URL.createObjectURL(file);
          window.open(fileURL);
          context.commit("SET_LOADING_STATUS_NODELAY", false);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    async createEvaluationPdfUrl(
      context,
      { propertyId, evaluationId, sessionKey }
    ) {
      await LiberatorService.createEvaluationPdfUrl(
        propertyId,
        evaluationId,
        sessionKey
      )
        .then(response => {
          return response.data;
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    async getEvaluationPdfUrl(
      context,
      { propertyId, evaluationId, sessionKey }
    ) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      return new Promise((resolve, reject) => {
        LiberatorService.getEvaluationPdfUrl(
          propertyId,
          evaluationId,
          sessionKey
        )
          .then(async response => {
            context.commit("SET_LOADING_STATUS_NODELAY", false);
            resolve(response);
          })
          .catch(async error => {
            context.commit("SET_LOADING_STATUS_NODELAY", false);
            reject(error);
            return handleError(context, error);
          });
      });
    },

    async getSearchTypes(context, { propertyId, evaluationId }) {
      await LiberatorService.getSearchTypes(propertyId, evaluationId)
        .then(response => {
          context.commit("SET_SEARCH_TYPES", response.data);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    updatePreferences(context, user) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      LiberatorService.updatePreferences(
        user.id,
        user.preferences.mlsMarket,
        user.preferences.state,
        user.preferences.evaluationSalePlusMinusSqft,
        user.preferences.evaluationSalePlusMinusYearBuilt,
        user.preferences.evaluationSaleMonthsClosed,
        user.preferences.evaluationRentPlusMinusSqft,
        user.preferences.evaluationRentPlusMinusYearBuilt,
        user.preferences.evaluationRentMonthsClosed,
        user.preferences.evaluationRadius,
        user.preferences.dealSurvey,
        user.preferences.dealAppraisal,
        user.preferences.dealInspection,
        user.preferences.dealPropertyInsurancePercentListPrice,
        user.preferences.dealPropertyTaxPercentListPrice,
        user.preferences.dealRepairs,
        user.preferences.dealMaxRefiCashback,
        user.preferences.conventionalDownPayment,
        user.preferences.conventionalInterestRate,
        user.preferences.conventionalLenderFeesPercentOfListPrice,
        user.preferences.conventionalMonthsTaxEscrow,
        user.preferences.conventionalLoanTermInYears,
        user.preferences.hardLoanToValuePercent,
        user.preferences.hardLenderFeesPercentOfListPrice,
        user.preferences.hardInterestRate,
        user.preferences.hardMonthsUntilRefi,
        user.preferences.hardRollInLenderFees,
        user.preferences.hardWeeksUntilLeased,
        user.preferences.hardRefiLoanToValue,
        user.preferences.hardRefiLoanTermInYears,
        user.preferences.hardRefiInterestRate,
        user.preferences.hardRefiLenderFeesPercentListPrice,
        user.preferences.hardRefiMonthsTaxEscrow,
        user.preferences.operatingExpenses,
        user.preferences.capitalImprovements,
        user.preferences.closingCosts,
        user.preferences.startupCosts,
        user.preferences.prepaidsAndDeposits
      )
        .then(async response => {
          var session = context.getters.session;
          session.user = response.data;
          await context.commit("SET_SESSION", session);
          await context.commit("SET_LOADING_STATUS_NODELAY", false);
        })
        .catch(error => {
          context.commit("SET_LOADING_STATUS_NODELAY", true);
          return handleError(context, error);
        });
    },

    async getMlsMarkets(context) {
      await LiberatorService.getMlsMarkets()
        .then(async response => {
          await context.commit("SET_MLS_MARKETS", response.data);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    async getStates(context) {
      await LiberatorService.getStates()
        .then(response => {
          context.commit("SET_STATES", response.data);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    async updateUser(context, user) {
      await LiberatorService.updateUser(
        user.id,
        user.email,
        user.password,
        user.firstName,
        user.lastName
      )
        .then(response => {
          context.commit("SET_SESSION_USER", response.data);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    async getChargifyUrl(context, userId) {
      await LiberatorService.getChargifyUrl(userId)
        .then(async response => {
          await context.commit("SET_CHARGIFY_URL", response.data);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    async getForSales(context, { state, cities, zips, daysBack }) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      await LiberatorService.getForSales(state, cities, zips, daysBack)
        .then(response => {
          context.commit("SET_FORSALES", response.data);
          context.commit("SET_LOADING_STATUS", false);
          context.dispatch("getSearchHistory", 50);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    async getSearchHistory(context, maxCount) {
      await LiberatorService.getSearchHistory(maxCount)
        .then(response => {
          context.commit("SET_SEARCH_HISTORY", response.data);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    async forgotPassword(context, email) {
      await LiberatorService.forgotPassword(email).catch(error => {
        if (error.response.status == 400)
          return errorMessage(
            context,
            `The email ${email} is not in the system, please double check your spelling`
          );
        else return handleError(context, error);
      });
    },

    async emailEvaluation(
      context,
      { propertyId, evaluationId, to, subject, message }
    ) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      return new Promise((resolve, reject) => {
        LiberatorService.emailEvaluation(
          propertyId,
          evaluationId,
          to,
          subject,
          message
        )
          .then(response => {
            resolve(response);
            context.commit("SET_LOADING_STATUS_NODELAY", false);
          })
          .catch(error => {
            reject(error);
            return handleError(context, error);
          });
      });
    },

    async getUserRights(context, user) {
      await LiberatorService.getUserRights(user.id)
        .then(async response => {
          var session = context.getters.session;
          session.rights = response.data;
          await context.commit("SET_SESSION", session);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    createSubscription(
      context,
      {
        email,
        password,
        firstName,
        lastName,
        firstNameOnCard,
        lastNameOnCard,
        ccNumber,
        expirationMonth,
        expirationYear,
        cvv,
        trialPeriod
      }
    ) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      return new Promise((resolve, reject) => {
        LiberatorService.createSubscription(
          email,
          password,
          firstName,
          lastName,
          firstNameOnCard,
          lastNameOnCard,
          ccNumber,
          expirationMonth,
          expirationYear,
          cvv,
          trialPeriod
        )
          .then(async response => {
            resolve(response);
            context.commit("SET_LOADING_STATUS_NODELAY", false);
          })
          .catch(async error => {
            context.commit("SET_LOADING_STATUS_NODELAY", false);
            reject(error);
            if (error && error.response && error.response.data)
              return errorMessage(context, error.response.data);
            else return handleError(context, error);
          });
      });
    },

    reactivateSubscription(
      context,
      {
        userId,
        firstNameOnCard,
        lastNameOnCard,
        ccNumber,
        expirationMonth,
        expirationYear,
        cvv
      }
    ) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      return new Promise((resolve, reject) => {
        LiberatorService.reactivateSubscription(
          userId,
          firstNameOnCard,
          lastNameOnCard,
          ccNumber,
          expirationMonth,
          expirationYear,
          cvv
        )
          .then(async response => {
            resolve(response);
            await context.commit("SET_LOADING_STATUS_NODELAY", false);
          })
          .catch(async error => {
            context.commit("SET_LOADING_STATUS_NODELAY", false);
            reject(error);
            if (error && error.response && error.response.data)
              return errorMessage(context, error.response.data);
            else return handleError(context, error);
          });
      });
    },

    getAllUsers(context) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      LiberatorService.getAllUsers()
        .then(response => {
          context.commit("SET_ALL_USERS", response.data);
          context.commit("SET_LOADING_STATUS_NODELAY", false);
        })
        .catch(error => {
          context.commit("SET_LOADING_STATUS_NODELAY", false);
          return handleError(context, error);
        });
    },

    updateUserRights(context, { userId, rights }) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      LiberatorService.updateUserRights(userId, rights)
        .then(response => {
          var data = response.data;
          context.commit("UPDATE_USER_RIGHTS", {
            userId,
            data
          });
          context.commit("SET_LOADING_STATUS_NODELAY", false);
        })
        .catch(error => {
          context.commit("SET_LOADING_STATUS_NODELAY", false);
          return handleError(context, error);
        });
    },

    async updateUserFromAdmin(context, user) {
      await LiberatorService.updateUser(
        user.id,
        user.email,
        user.password,
        user.firstName,
        user.lastName
      ).catch(error => {
        return handleError(context, error);
      });
    },

    async getDefaultPreferences(context) {
      await LiberatorService.getDefaultPreferences()
        .then(async response => {
          await context.commit("SET_DEFAULT_PREFERENCES", response.data);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    async resetPreferences(context, userId) {
      await LiberatorService.resetPreferences(userId)
        .then(async response => {
          var session = context.getters.session;
          session.user = response.data;
          await context.commit("SET_SESSION", session);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    async getEvaluationUsage(context, { startDate, endDate }) {
      await LiberatorService.getEvaluationUsage(startDate, endDate)
        .then(response => {
          context.commit("SET_EVALUATION_USAGE", response.data);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    newSubscriptionEmployee(
      context,
      { firstName, lastName, email, password, isAgent }
    ) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      return new Promise((resolve, reject) => {
        LiberatorService.newSubscriptionEmployee(
          firstName,
          lastName,
          email,
          password,
          isAgent
        )
          .then(response => {
            context.commit("SET_NEW_USER_ACTIVE", false);
            context.commit("SET_LOADING_STATUS_NODELAY", false);
            resolve(response);
            context.dispatch("getAllUsers");
          })
          .catch(error => {
            context.commit("SET_LOADING_STATUS_NODELAY", false);
            reject(error);
            return handleError(context, error);
          });
      });
    },

    async getNotes(context, { userId, includeInactive }) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      await LiberatorService.getNotes(userId, includeInactive)
        .then(response => {
          context.commit("SET_LOADING_STATUS_NODELAY", false);
          context.commit("SET_NOTES", response.data);
        })
        .catch(error => {
          context.commit("SET_LOADING_STATUS_NODELAY", false);
          return handleError(context, error);
        });
    },

    async discoveryMigrate(
      context,
      {
        username,
        password,
        ccNumber,
        firstNameOnCard,
        lastNameOnCard,
        expirationMonth,
        expirationYear,
        cvv
      }
    ) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      return await new Promise(async (resolve, reject) => {
        await LiberatorService.discoveryMigrate(
          username,
          password,
          ccNumber,
          firstNameOnCard,
          lastNameOnCard,
          expirationMonth,
          expirationYear,
          cvv
        )
          .then(response => {
            context.commit("SET_LOADING_STATUS_NODELAY", false);
            resolve(response);
          })
          .catch(error => {
            context.commit("SET_LOADING_STATUS_NODELAY", false);
            reject(error);
            return handleError(context, error);
          });
      });
    },

    async updateEvaluationsPropertyAttributes(
      context,
      {
        propertyId,
        evaluationId,
        baths,
        beds,
        county,
        garage,
        hoaAnnual,
        listDate,
        listPrice,
        mlsMarket,
        sqft,
        subdivision,
        taxesAnnual,
        yearBuilt
      }
    ) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      await LiberatorService.updateEvaluationsPropertyAttributes(
        propertyId,
        evaluationId,
        baths,
        beds,
        county,
        garage,
        hoaAnnual,
        listDate,
        listPrice,
        mlsMarket,
        sqft,
        subdivision,
        taxesAnnual,
        yearBuilt
      )
        .then(response => {
          context.commit("SET_LOADING_STATUS_NODELAY", false);
          context.commit("UPDATE_EVALUATION", response.data);
        })
        .catch(error => {
          context.commit("SET_LOADING_STATUS_NODELAY", false);
          return handleError(context, error);
        });
    },

    async newListing(context, { propertyId, listing }) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      await LiberatorService.newListing(
        propertyId,
        listing.applicationPolicy,
        listing.availableDate,
        listing.baths,
        listing.beds,
        listing.city,
        listing.depositPolicy,
        listing.description,
        listing.features,
        listing.isActive,
        listing.mapUrl,
        listing.petPolicy,
        listing.pictureUrls,
        listing.rentPolicy,
        listing.sqft,
        listing.state,
        listing.street,
        listing.title,
        listing.zip
      )
        .then(response => {
          response.data.availableDate = new moment(
            response.data.availableDate
          ).toDate();
          context.commit("ADD_LISTING", response.data);
          context.commit("SET_LOADING_STATUS_NODELAY", false);
        })
        .catch(error => {
          context.commit("SET_LOADING_STATUS_NODELAY", false);
          return handleError(context, error);
        });
    },

    async editListing(context, { propertyId, listing }) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      await LiberatorService.editListing(
        propertyId,
        listing.id,
        listing.applicationPolicy,
        listing.availableDate,
        listing.baths,
        listing.beds,
        listing.city,
        listing.depositPolicy,
        listing.description,
        listing.features,
        listing.isActive,
        listing.mapUrl,
        listing.petPolicy,
        listing.pictureUrls,
        listing.rentPolicy,
        listing.sqft,
        listing.state,
        listing.street,
        listing.title,
        listing.zip
      )
        .then(response => {
          response.data.availableDate = new moment(
            response.data.availableDate
          ).toDate();
          context.commit("UPDATE_LISTING", response.data);
          context.commit("SET_LOADING_STATUS_NODELAY", false);
        })
        .catch(error => {
          context.commit("SET_LOADING_STATUS_NODELAY", false);
          return handleError(context, error);
        });
    },

    async addPicturesToListing(context, { propertyId, listingId, s3Keys }) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      await LiberatorService.addPicturesToListing(propertyId, listingId, s3Keys)
        .then(response => {
          response.data.availableDate = new moment(
            response.data.availableDate
          ).toDate();
          context.commit("UPDATE_LISTING", response.data);
          context.commit("SET_LOADING_STATUS_NODELAY", false);
        })
        .catch(error => {
          context.commit("SET_LOADING_STATUS_NODELAY", false);
          return handleError(context, error);
        });
    },

    async deletePictureFromListing(
      context,
      { propertyId, listingId, pictureUrl }
    ) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      await LiberatorService.deletePictureFromListing(
        propertyId,
        listingId,
        pictureUrl
      )
        .then(response => {
          response.data.availableDate = new moment(
            response.data.availableDate
          ).toDate();
          context.commit("UPDATE_LISTING", response.data);
          context.commit("SET_LOADING_STATUS_NODELAY", false);
        })
        .catch(error => {
          context.commit("SET_LOADING_STATUS_NODELAY", false);
          return handleError(context, error);
        });
    },

    async getListing(context, { propertyId, listingId }) {
      await LiberatorService.getListing(propertyId, listingId)
        .then(response => {
          response.data.availableDate = new moment(
            response.data.availableDate
          ).toDate();
          context.commit("SET_LISTING", response.data);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    async getListingPublicLink(context, { propertyId, listingId }) {
      await LiberatorService.getListing(propertyId, listingId)
        .then(response => {
          context.commit("SET_LISTING", response.data);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    async createBPO(context, propertyId) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      await LiberatorService.createBPO(propertyId)
        .then(response => {
          context.commit("SET_BPO", response.data);
          context.commit("SET_LOADING_STATUS_NODELAY", false);
        })
        .catch(error => {
          context.commit("SET_LOADING_STATUS_NODELAY", false);
          return handleError(context, error);
        });
    },

    async getBPO(context, { propertyId, bpoId }) {
      context.commit("SET_LOADING_STATUS", true);
      await LiberatorService.getBPO(propertyId, bpoId)
        .then(response => {
          context.commit("SET_BPO", response.data);
          context.commit("SET_LOADING_STATUS", false);
        })
        .catch(error => {
          context.commit("SET_LOADING_STATUS", false);
          return handleError(context, error);
        });
    },

    async updateBpo(context, { propertyId, bpoId, bpo }) {
      return await new Promise(async (resolve, reject) => {
        await LiberatorService.updateBpo(propertyId, bpoId, bpo)
          .then(response => {
            resolve(response);
          })
          .catch(error => {
            reject(error);
            return handleError(context, error);
          });
      });
    },

    async getBpoByGuid(context, { propertyId, bpoGuid }) {
      context.commit("SET_LOADING_STATUS", true);
      await LiberatorService.getBpoByGuid(propertyId, bpoGuid)
        .then(response => {
          context.commit("SET_BPO_BY_GUID", response.data);
          context.commit("SET_LOADING_STATUS", false);
        })
        .catch(error => {
          context.commit("SET_LOADING_STATUS", false);
          return handleError(context, error);
        });
    },

    async getBpo2ByGuid(context, { propertyId, bpo2Guid }) {
      context.commit("SET_LOADING_STATUS", true);
      await LiberatorService.getBpo2ByGuid(propertyId, bpo2Guid)
        .then(response => {
          context.commit("SET_BPO2_BY_GUID", response.data);
          context.commit("SET_LOADING_STATUS", false);
        })
        .catch(error => {
          context.commit("SET_LOADING_STATUS", false);
          return handleError(context, error);
        });
    },

    async copyBpo(context, { propertyId, bpoId }) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      await LiberatorService.copyBpo(propertyId, bpoId)
        .then(response => {
          context.commit("INSERT_EVALUATION", response.data);
          context.commit("SET_LOADING_STATUS", false);
        })
        .catch(error => {
          context.commit("SET_LOADING_STATUS", false);
          return handleError(context, error);
        });
    },

    async emailBpo(context, { propertyId, bpoId, to, subject, message }) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      return new Promise((resolve, reject) => {
        LiberatorService.emailBpo(propertyId, bpoId, to, subject, message)
          .then(response => {
            resolve(response);
            context.commit("SET_LOADING_STATUS_NODELAY", false);
          })
          .catch(error => {
            reject(error);
            return handleError(context, error);
          });
      });
    },

    async emailBpo2(context, { propertyId, bpo2Id, to, subject, message }) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      return new Promise((resolve, reject) => {
        LiberatorService.emailBpo2(propertyId, bpo2Id, to, subject, message)
          .then(response => {
            resolve(response);
            context.commit("SET_LOADING_STATUS_NODELAY", false);
          })
          .catch(error => {
            reject(error);
            return handleError(context, error);
          });
      });
    },

    async deleteBpo(context, bpo) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      await LiberatorService.deleteBpo(bpo.propertyId, bpo.id)
        .then(response => {
          context.commit("DELETE_EVALUATION", response.data);
          context.commit("SET_LOADING_STATUS", false);
        })
        .catch(error => {
          context.commit("SET_LOADING_STATUS", false);
          return handleError(context, error);
        });
    },

    async getBpoPdf(context, bpo) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);

      return new Promise((resolve, reject) => {
        LiberatorService.getBpoPdf(bpo.propertyId, bpo.guid)
          .then(async response => {
            context.commit("SET_LOADING_STATUS_NODELAY", false);
            resolve(response);
          })
          .catch(async error => {
            context.commit("SET_LOADING_STATUS_NODELAY", false);
            reject(error);
            return handleError(context, error);
          });
      });
    },

    async getBpo2Pdf(context, bpo2) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);

      return new Promise((resolve, reject) => {
        LiberatorService.getBpo2Pdf(bpo2.propertyId, bpo2.guid)
          .then(async response => {
            context.commit("SET_LOADING_STATUS_NODELAY", false);
            resolve(response);
          })
          .catch(async error => {
            context.commit("SET_LOADING_STATUS_NODELAY", false);
            reject(error);
            return handleError(context, error);
          });
      });
    },

    async createBPO2FromScratch(context, propertyId) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      await LiberatorService.createBPO2FromScratch(propertyId)
        .then(response => {
          context.commit("SET_BPO2", response.data);
          context.commit("INSERT_EVALUATION", response.data);
          context.commit("SET_LOADING_STATUS_NODELAY", false);
        })
        .catch(error => {
          context.commit("SET_LOADING_STATUS_NODELAY", false);
          return handleError(context, error);
        });
    },

    async createBpo2(context, { propertyId, bpoId }) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      await LiberatorService.createBpo2(propertyId, bpoId)
        .then(response => {
          context.commit("SET_BPO2", response.data);
          context.commit("INSERT_EVALUATION", response.data);
          context.commit("SET_LOADING_STATUS_NODELAY", false);
        })
        .catch(error => {
          context.commit("SET_LOADING_STATUS_NODELAY", false);
          return handleError(context, error);
        });
    },

    async deleteBpo2(context, bpo2) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      await LiberatorService.deleteBpo2(bpo2.propertyId, bpo2.id)
        .then(response => {
          context.commit("DELETE_EVALUATION", response.data);
          context.commit("SET_LOADING_STATUS", false);
        })
        .catch(error => {
          context.commit("SET_LOADING_STATUS", false);
          return handleError(context, error);
        });
    },

    async getBPO2(context, { propertyId, bpo2Id }) {
      context.commit("SET_LOADING_STATUS", true);
      await LiberatorService.getBPO2(propertyId, bpo2Id)
        .then(response => {
          context.commit("SET_BPO2", response.data);
          context.commit("SET_LOADING_STATUS", false);
        })
        .catch(error => {
          context.commit("SET_LOADING_STATUS", false);
          return handleError(context, error);
        });
    },

    async updateBpo2(context, { propertyId, bpo2Id, bpo2 }) {
      return await new Promise(async (resolve, reject) => {
        await LiberatorService.updateBpo2(propertyId, bpo2Id, bpo2)
          .then(response => {
            resolve(response);
          })
          .catch(error => {
            reject(error);
            return handleError(context, error);
          });
      });
    },

    getNaaSurveys(context) {
      LiberatorService.getNaaSurveys()
        .then(response => {
          context.commit("SET_NAA_SURVEYS", response.data);
        })
        .catch(error => {
          return handleError(context, error);
        });
    },

    async copyBpo2(context, { propertyId, bpo2Id }) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      await LiberatorService.copyBpo2(propertyId, bpo2Id)
        .then(response => {
          context.commit("INSERT_EVALUATION", response.data);
          context.commit("SET_LOADING_STATUS", false);
        })
        .catch(error => {
          context.commit("SET_LOADING_STATUS", false);
          return handleError(context, error);
        });
    },

    async createLongFormFromScratch(context, propertyId) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      await LiberatorService.createLongFormFromScratch(propertyId)
        .then(response => {
          context.commit("SET_LONGFORM", response.data);
          context.commit("INSERT_EVALUATION", response.data);
          context.commit("SET_LOADING_STATUS_NODELAY", false);
        })
        .catch(error => {
          context.commit("SET_LOADING_STATUS_NODELAY", false);
          return handleError(context, error);
        });
    },

    async createLongFormFromBpo2(context, { propertyId, bpo2Id }) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      await LiberatorService.createLongFormFromBpo2(propertyId, bpo2Id)
        .then(response => {
          context.commit("SET_LONGFORM", response.data);
          context.commit("INSERT_EVALUATION", response.data);
          context.commit("SET_LOADING_STATUS_NODELAY", false);
        })
        .catch(error => {
          context.commit("SET_LOADING_STATUS_NODELAY", false);
          return handleError(context, error);
        });
    },

    async getLongform(context, { propertyId, longformId }) {
      context.commit("SET_LOADING_STATUS", true);
      await LiberatorService.getLongform(propertyId, longformId)
        .then(response => {
          context.commit("SET_LONGFORM", response.data);
          context.commit("SET_LOADING_STATUS", false);
        })
        .catch(error => {
          context.commit("SET_LOADING_STATUS", false);
          return handleError(context, error);
        });
    },

    async deleteLongform(context, longform) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      await LiberatorService.deleteLongform(longform.propertyId, longform.id)
        .then(response => {
          context.commit("DELETE_EVALUATION", response.data);
          context.commit("SET_LOADING_STATUS", false);
        })
        .catch(error => {
          context.commit("SET_LOADING_STATUS", false);
          return handleError(context, error);
        });
    },

    async updateLongform(context, { propertyId, longformId, longform }) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      return await new Promise(async (resolve, reject) => {
        await LiberatorService.updateLongform(propertyId, longformId, longform)
          .then(response => {
            context.commit("SET_LONGFORM", response.data);
            context.commit("SET_LOADING_STATUS", false);
            resolve(response);
          })
          .catch(error => {
            context.commit("SET_LOADING_STATUS", false);
            reject(error);
            return handleError(context, error);
          });
      });
    },

    async createRentComps(context, { propertyId, rents }) {
      context.commit("SET_LOADING_STATUS_NODELAY", true);
      return await new Promise(async (resolve, reject) => {
        await LiberatorService.createRentComps(propertyId, rents)
          .then(response => {
            context.commit("SET_RENTCOMPS", response.data);
            context.commit("SET_LOADING_STATUS", false);
            resolve(response);
          })
          .catch(error => {
            context.commit("SET_LOADING_STATUS", false);
            reject(error);
            return handleError(context, error);
          });
      });
    },

    async bpoPublishField(
      context,
      { propertyId, bpoId, bpo, field, instanceGuid, timeStamp }
    ) {
      return await new Promise(async (resolve, reject) => {
        await LiberatorService.bpoPublishField(
          propertyId,
          bpoId,
          bpo,
          field,
          instanceGuid,
          timeStamp
        )
          .then(response => {
            resolve(response);
          })
          .catch(error => {
            reject(error);
          });
      });
    },

    async bpo2PublishField(
      context,
      { propertyId, bpo2Id, bpo2, field, instanceGuid, timeStamp }
    ) {
      return await new Promise(async (resolve, reject) => {
        await LiberatorService.bpo2PublishField(
          propertyId,
          bpo2Id,
          bpo2,
          field,
          instanceGuid,
          timeStamp
        )
          .then(response => {
            resolve(response);
          })
          .catch(error => {
            reject(error);
          });
      });
    }
  }
});
